import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import LandingArchiveIndex from '../component/landing/LandingArchiveIndex';
import {
  buildArchiveIndexGroups,
  type LandingArchiveCandidate,
} from '../shared/util/landingArchiveIndex';
import { useDeviceStore } from '../shared/store/deviceStore';
import { DeviceType } from '../shared/enum/EnumRepository';
import { useLandingMember } from '../shared/hooks/useApi/useLandingMember';
import {
  DEFAULT_LANDING_ARCHIVE_FALLBACK,
  useLandingMemberStore,
} from '../shared/store/landingMemberStore';
import { useNavigate } from 'react-router-dom';
import { useOptimizedImageUrl } from '../shared/hooks/useOptimizedImageUrl';

const ARCHIVE_PREVIEW_FADE_IN_MS = 400;
/** 다른 이미지로 교체될 때 나가는 쪽 */
const ARCHIVE_PREVIEW_FADE_OUT_REPLACE_MS = 500;
/** hover 해제 등으로 완전히 사라질 때 */
const ARCHIVE_PREVIEW_FADE_OUT_HIDE_MS = 800;
/** leave 후 페이드아웃 시작까지 대기. 목록 사이 빈 틈에서 깜빡임 완화 */
const ARCHIVE_PREVIEW_HIDE_DELAY_MS = 500;

type PreviewSlotIndex = 0 | 1;

type PreviewSlotState = {
  originUrl: string;
  loaded: boolean;
};

interface ArchivePreviewSlotProps {
  originUrl: string;
  shown: boolean;
  /** 프리뷰 자체가 켜져 있는지. false면 hide fade-out(길게), true면 교체 fade-out(짧게) */
  previewVisible: boolean;
  containerRef: React.RefObject<HTMLDivElement | null>;
  onLoadedChange: (loaded: boolean) => void;
}

const ArchivePreviewSlot: React.FC<ArchivePreviewSlotProps> = ({
  originUrl,
  shown,
  previewVisible,
  containerRef,
  onLoadedChange,
}) => {
  const imageUrl = useOptimizedImageUrl(originUrl, containerRef, {
    preferThumbnail: false,
  });
  const [loadedForOrigin, setLoadedForOrigin] = useState('');
  const [decodedUrl, setDecodedUrl] = useState('');

  if (originUrl !== loadedForOrigin) {
    setLoadedForOrigin(originUrl);
    setDecodedUrl('');
  }

  const isLoaded = Boolean(originUrl) && imageUrl !== '' && decodedUrl === imageUrl;
  const isVisible = shown && isLoaded;
  const fadeMs = isVisible
    ? ARCHIVE_PREVIEW_FADE_IN_MS
    : previewVisible
      ? ARCHIVE_PREVIEW_FADE_OUT_REPLACE_MS
      : ARCHIVE_PREVIEW_FADE_OUT_HIDE_MS;

  useEffect(() => {
    onLoadedChange(isLoaded);
  }, [isLoaded, onLoadedChange]);

  if (!originUrl || !imageUrl) return null;

  return (
    <ArchivePreviewImage
      src={imageUrl}
      alt=""
      draggable={false}
      decoding="async"
      loading="eager"
      $isVisible={isVisible}
      $fadeMs={fadeMs}
      onLoad={() => setDecodedUrl(imageUrl)}
      onError={() => setDecodedUrl('')}
    />
  );
};

interface ArchiveIndexPreviewProps {
  originUrl: string;
  visible: boolean;
}

/** 2-slot crossfade: 이전 이미지 유지 + 로드 완료 후 전환. key remount 없음. */
const ArchiveIndexPreview: React.FC<ArchiveIndexPreviewProps> = ({ originUrl, visible }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [slots, setSlots] = useState<[PreviewSlotState, PreviewSlotState]>([
    { originUrl: '', loaded: false },
    { originUrl: '', loaded: false },
  ]);
  const [activeSlot, setActiveSlot] = useState<PreviewSlotIndex>(0);

  const setSlot0Loaded = useCallback((loaded: boolean) => {
    setSlots(([a, b]) => (a.loaded === loaded ? [a, b] : [{ ...a, loaded }, b]));
  }, []);

  const setSlot1Loaded = useCallback((loaded: boolean) => {
    setSlots(([a, b]) => (b.loaded === loaded ? [a, b] : [a, { ...b, loaded }]));
  }, []);

  useEffect(() => {
    const target = originUrl.trim();
    if (!target) return;

    setSlots((prev) => {
      if (prev[0].originUrl === target || prev[1].originUrl === target) return prev;

      const next: [PreviewSlotState, PreviewSlotState] = [
        { ...prev[0] },
        { ...prev[1] },
      ];

      if (!prev[activeSlot].originUrl) {
        next[activeSlot] = { originUrl: target, loaded: false };
      } else {
        const inactive: PreviewSlotIndex = activeSlot === 0 ? 1 : 0;
        next[inactive] = { originUrl: target, loaded: false };
      }
      return next;
    });
  }, [originUrl, activeSlot]);

  useEffect(() => {
    const target = originUrl.trim();
    if (!target || !visible) return;

    if (slots[0].originUrl === target && slots[0].loaded && activeSlot !== 0) {
      setActiveSlot(0);
      return;
    }
    if (slots[1].originUrl === target && slots[1].loaded && activeSlot !== 1) {
      setActiveSlot(1);
    }
  }, [originUrl, visible, slots, activeSlot]);

  return (
    <ArchivePreviewFrame ref={containerRef} aria-hidden="true">
      <ArchivePreviewSlot
        originUrl={slots[0].originUrl}
        shown={visible && activeSlot === 0}
        previewVisible={visible}
        containerRef={containerRef}
        onLoadedChange={setSlot0Loaded}
      />
      <ArchivePreviewSlot
        originUrl={slots[1].originUrl}
        shown={visible && activeSlot === 1}
        previewVisible={visible}
        containerRef={containerRef}
        onLoadedChange={setSlot1Loaded}
      />
    </ArchivePreviewFrame>
  );
};

const Landing: React.FC = () => {
  const { deviceType } = useDeviceStore();
  const isMobile = deviceType === DeviceType.MOBILE;
  const { getArchiveIndex } = useLandingMember();
  const navigate = useNavigate();
  const archiveIndexList = useLandingMemberStore((s) => s.archiveIndexList);
  const [previewCandidate, setPreviewCandidate] = useState<LandingArchiveCandidate | null>(null);
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const previewClearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    getArchiveIndex();
  }, []);

  const archiveIndexGroups = useMemo(() => {
    const archiveBase =
      archiveIndexList.length > 0 ? archiveIndexList : DEFAULT_LANDING_ARCHIVE_FALLBACK;

    return buildArchiveIndexGroups(archiveBase);
  }, [archiveIndexList]);

  const openCandidateArchive = useCallback(
    (candidate: LandingArchiveCandidate) => {
      if (!candidate.handle?.trim()) return;
      navigate(`/@${candidate.handle}/`);
    },
    [navigate]
  );

  const clearPreviewTimer = useCallback(() => {
    if (previewClearTimerRef.current == null) return;
    clearTimeout(previewClearTimerRef.current);
    previewClearTimerRef.current = null;
  }, []);

  const handleCandidatePreviewChange = useCallback(
    (candidate: LandingArchiveCandidate | null) => {
      clearPreviewTimer();

      if (candidate?.originUrl?.trim()) {
        setPreviewCandidate(candidate);
        setIsPreviewVisible(true);
        return;
      }

      previewClearTimerRef.current = setTimeout(() => {
        setIsPreviewVisible(false);
        previewClearTimerRef.current = setTimeout(() => {
          setPreviewCandidate(null);
          previewClearTimerRef.current = null;
        }, ARCHIVE_PREVIEW_FADE_OUT_HIDE_MS);
      }, ARCHIVE_PREVIEW_HIDE_DELAY_MS);
    },
    [clearPreviewTimer]
  );

  useEffect(() => clearPreviewTimer, [clearPreviewTimer]);

  const previewOriginUrl = previewCandidate?.originUrl?.trim() ?? '';

  return (
    <LandingContainer>
      <ArchiveIndexPreview
        originUrl={previewOriginUrl}
        visible={isPreviewVisible && Boolean(previewOriginUrl)}
      />
      <ArchiveContainer $isMobile={isMobile}>
        <LandingArchiveIndex
          groups={archiveIndexGroups}
          onCandidateActivate={openCandidateArchive}
          onCandidatePreviewChange={handleCandidatePreviewChange}
        />
      </ArchiveContainer>
    </LandingContainer>
  );
};

const LandingContainer = styled.main`
  position: relative;
  isolation: isolate;
  width: 100%;
  height: 100%;
  max-height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  overflow: hidden;
  box-sizing: border-box;
`;

const ArchiveContainer = styled.div<{ $isMobile: boolean }>`
  position: relative;
  z-index: 1;
  width: 100%;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  box-sizing: border-box;
  overflow-x: auto;
  overflow-y: hidden;

  margin-top: ${({ $isMobile }) =>
    $isMobile ? 'clamp(100px, 10vh, 120px)' : 'clamp(120px, 15vh, 150px)'};

  padding: clamp(20px, 3vw, 50px) clamp(20px, 4vw, 30px)
    ${({ $isMobile }) => ($isMobile ? '25px' : '50px')};

  &::-webkit-scrollbar {
    display: none;
  }
`;

const ArchivePreviewFrame = styled.div`
  position: absolute;
  top: 60%;
  left: 63%;
  width: 40vw;
  height: 60vh;
  transform: translate(-50%, -50%);
  z-index: 0;
  pointer-events: none;

  @media (any-hover: none) {
    display: none;
  }
`;

const ArchivePreviewImage = styled.img<{ $isVisible: boolean; $fadeMs: number }>`
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  user-select: none;
  will-change: opacity;
  transition: opacity ${({ $fadeMs }) => $fadeMs}ms ease-in-out;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export default Landing;
